// AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { auth, firebase, db } from './utils/firebase';
import { collection, doc, getDoc, getDocs, addDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
// import { getDetailsByUid } from './utils/utils';
// import { getPatientByUid, updatePatientData } from './utils/firebase_api';
// import { getPatientMeetings } from './utils/firebase_api';
import axios from 'axios';
import moment from 'moment';
// import { getDrFromDxOrDr, maybeNavigateToFollowUp, maybeNavigateToSelectFollowUpType } from './utils/utils';
import { functions } from './utils/firebase';
import { httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

function getCookie(name) {
  const nameEQ = `${name}=`;
  const cookiesArray = document.cookie.split(';');
  for (let i = 0; i < cookiesArray.length; i++) {
    let cookie = cookiesArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1); // Remove leading spaces
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }
  return null;
};

// function useQuery() {
//   return new URLSearchParams(useLocation().search);
// }

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Session lock additions
  const [sessionOwner, setSessionOwner] = useState(null);
  const [showTakeoverPrompt, setShowTakeoverPrompt] = useState(false);
  function getOrCreateSessionId() {
    const key = 'joyous_session_id';
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = uuidv4();
      sessionStorage.setItem(key, id);
    }
    return id;
  }
  const mySessionId = useRef(getOrCreateSessionId());
  const [hasSession, setHasSession] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  // The cookie is written in the redirect from a new meeting with magic id
  // const getCookie = name => {
  //   const value = `; ${document.cookie}`;
  //   const parts = value.split(`; ${name}=`);
  //   if (parts.length === 2) return parts.pop().split(';').shift();
  // };

  // Cache the Firebase ID token for beacon use
  const idTokenRef = useRef(null);

  // Auto-close PiP window if session is lost or takeover is prompted
  useEffect(() => {
    async function closePipIfNeeded() {
      if ((!hasSession || showTakeoverPrompt) && window.joyPip) {
        const { closeGlobalPiPWindow } = await import('./components/MyVideoConference');
        closeGlobalPiPWindow();
      }
    }
    closePipIfNeeded();
  }, [hasSession, showTakeoverPrompt]);
  useEffect(() => {
    let isMounted = true;
    if (currentUser) {
      currentUser.getIdToken().then(token => {
        if (isMounted) idTokenRef.current = token;
      });
    } else {
      idTokenRef.current = null;
    }
    return () => { isMounted = false; };
  }, [currentUser]);

  useEffect(() => {
    /**
     * We use navigator.sendBeacon here to reliably notify the backend (Firebase Cloud Function)
     * when the user closes or reloads the tab. Browsers do not guarantee that asynchronous
     * operations (like Firestore writes) in beforeunload/unload handlers will complete before
     * the page is closed. sendBeacon is designed for this use case: it sends a small POST request
     * in the background, even as the page is unloading, ensuring the session is cleared server-side.
     */
    // On tab/window close, clear Firestore session if this tab owns it using a beacon
    const handleUnload = () => {
      if (!currentUser || !idTokenRef.current) return;
      const url = 'https://us-central1-joyous-web.cloudfunctions.net/v3/nurse_app_clear_session'; // TODO: Replace with your deployed function URL
      const payload = JSON.stringify({
        uid: currentUser.uid,
        sessionId: mySessionId.current,
        idToken: idTokenRef.current,
      });
      navigator.sendBeacon(url, payload);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [currentUser, mySessionId]);

  useEffect(() => {
    console.log('Setting up auth listener');
    auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(() => {
        console.log('Auth persistence set to local');
        // Check for the current auth state
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
          console.log('user', user);
          if (user) {
            console.log('user', user);
            const hasAccessDocRef = doc(db, 'on_demand_access', user.uid);

            // Listen for real-time session changes
            const unsubscribeSession = onSnapshot(hasAccessDocRef, (snapshot) => {
              const data = snapshot.data();
              console.log('data', data);
              if (!data) return;
              setSessionOwner(data.currentSessionId);

              if (data.enabled === true) {
                setCurrentUser(user);
                setIsAuthorized(true);

                // Session lock logic
                if (!data.currentSessionId || data.currentSessionId === mySessionId.current) {
                  setHasSession(true);
                  setShowTakeoverPrompt(false);
                } else {
                  setHasSession(false);
                  setShowTakeoverPrompt(true);
                }
              } else {
                setIsAuthorized(false);
                setCurrentUser(null);
                setHasSession(false);
              }
              setIsLoading(false);
            });

            // Attempt to claim session on login
            const claimSession = async () => {
              const sessionSnap = await getDoc(hasAccessDocRef);
              const data = sessionSnap.data();
              if (!data || !data.currentSessionId || data.currentSessionId === mySessionId.current) {
                try {
                  await setDoc(hasAccessDocRef, { currentSessionId: mySessionId.current }, { merge: true });
                  setHasSession(true);
                } catch (error) {
                  console.error('Failed to claim session:', error);
                  setHasSession(false);
                  setShowTakeoverPrompt(false);
                  setSessionError('Failed to claim session. Please check your connection or permissions.');
                }
              } else {
                setHasSession(false);
                setShowTakeoverPrompt(true);
              }
            };
            claimSession();

            return () => {
              unsubscribeSession();
            };
          } else {
            setIsAuthorized(false);
            setCurrentUser(null);
            setHasSession(false);
          }
          setIsLoading(false);
        });
        return () => unsubscribe();
      })
      .catch((error) => {
        console.error("Error setting persistence:", error);
        setIsLoading(false);
      });

    let tkn = getCookie('telejoyToken');
    if (tkn) {
      auth.signInWithCustomToken(tkn);
      // document.cookie = 'token=;';
    };


    // const unsubscribe = auth.onAuthStateChanged(async (user) => {
    //   setCurrentUser(user);  // Store the full user object, including uid
    //   // console.log('uid', user?.uid);

    //   setIsLoading(false);
    // });

    // let tkn = getCookie('token');
    // if (tkn) {
    //   auth.signInWithCustomToken(tkn);
    //   document.cookie = 'token=;';
    // };

    // return unsubscribe; // Clean up the listener on component unmount
  }, []);

  // Handle session takeover
  const handleTakeover = async () => {
    if (!currentUser) return;
    const hasAccessDocRef = doc(db, 'on_demand_access', currentUser.uid);
    try {
      await setDoc(hasAccessDocRef, { currentSessionId: mySessionId.current }, { merge: true });
      setHasSession(true);
      setShowTakeoverPrompt(false);
    } catch (error) {
      console.error('Failed to take over session:', error);
      setSessionError('Failed to take over session. Please check your connection or permissions.');
    }
  };


  if (isLoading) return <div>Loading...</div>;

  return (
    <AuthContext.Provider value={{
      currentUser,
      isLoading,
      isAuthorized,
    }}>
      {/* Session error overlay */}
      {sessionError && (
        <div className="text-red-600 p-4 fixed top-0 left-0 w-full z-50 bg-white shadow">{sessionError}</div>
      )}
      {/* Session takeover modal overlay, blocks app content */}
      {showTakeoverPrompt ? (
        <div className="modal fixed top-0 left-0 w-full h-full flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded shadow">
            <p>Telejoy is already open elsewhere. Switch to this window?</p>
            <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={handleTakeover}>Switch</button>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
